/**
 * Medication List Page
 *
 * eMAR workflow for medication support, PRN protocols, safety checks,
 * refused/held/missed records and MAR audit history.
 */

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  ClipboardCheck,
  FileText,
  Pill,
  ShieldCheck,
  Thermometer,
  XCircle,
} from 'lucide-react';
import { localMedications } from '@/core/services/local-api-fallback';

type MedicationStatus = 'ACTIVE' | 'DISCONTINUED' | 'ON_HOLD';
type AdministrationStatus = 'GIVEN' | 'REFUSED' | 'HELD' | 'MISSED';
type MedicationRoute = 'ORAL' | 'TOPICAL' | 'INJECTION' | 'INHALATION' | 'OTHER';

interface Medication {
  id: string;
  clientId: string;
  medicationName: string;
  genericName?: string;
  dosage: string;
  route: MedicationRoute;
  frequency: string;
  instructions?: string;
  prescribedBy: string;
  prescribedDate: string;
  startDate: string;
  endDate?: string;
  status: MedicationStatus;
  refillsRemaining?: number;
  lastAdministered?: string;
  nextScheduledTime?: string;
  sideEffects?: string[];
  warnings?: string[];
  prnProtocol?: string;
  maxDailyDose?: string;
  safetyChecks?: string[];
}

interface MedicationAdministration {
  id: string;
  medicationId: string;
  clientId: string;
  medicationName: string;
  administeredBy: string;
  administeredAt: string;
  dosageGiven: string;
  status: AdministrationStatus;
  notes?: string;
  refusalReason?: string;
  prnReason?: string;
  painScore?: string;
  bodyTemperature?: string;
  effectivenessReview?: string;
  followUpDueAt?: string;
}

interface AdministrationForm {
  status: AdministrationStatus;
  prnReason: string;
  painScore: string;
  bodyTemperature: string;
  refusalReason: string;
  notes: string;
  effectivenessReview: string;
  checkedIdentity: boolean;
  checkedMedicine: boolean;
  checkedDose: boolean;
  checkedRoute: boolean;
  checkedTime: boolean;
  checkedWarnings: boolean;
}

async function fetchMedications(clientId: string): Promise<Medication[]> {
  if (import.meta.env.VITE_LOCAL_DATA_MODE === 'true') {
    return localMedications.filter((medication) => medication.clientId === clientId) as Medication[];
  }

  const response = await fetch(`/api/clients/${clientId}/medications`, {
    credentials: 'include',
  });
  if (!response.ok) throw new Error('Failed to fetch medications');
  return response.json();
}

const initialForm: AdministrationForm = {
  status: 'GIVEN',
  prnReason: '',
  painScore: '',
  bodyTemperature: '',
  refusalReason: '',
  notes: '',
  effectivenessReview: '',
  checkedIdentity: false,
  checkedMedicine: false,
  checkedDose: false,
  checkedRoute: false,
  checkedTime: false,
  checkedWarnings: false,
};

const safetyFields: Array<keyof Pick<
  AdministrationForm,
  | 'checkedIdentity'
  | 'checkedMedicine'
  | 'checkedDose'
  | 'checkedRoute'
  | 'checkedTime'
  | 'checkedWarnings'
>> = [
  'checkedIdentity',
  'checkedMedicine',
  'checkedDose',
  'checkedRoute',
  'checkedTime',
  'checkedWarnings',
];

const safetyLabels: Record<(typeof safetyFields)[number], string> = {
  checkedIdentity: 'Right person confirmed',
  checkedMedicine: 'Right medicine checked',
  checkedDose: 'Right dose checked',
  checkedRoute: 'Right route checked',
  checkedTime: 'Right time checked',
  checkedWarnings: 'Warnings and allergies reviewed',
};

const statusStyles: Record<AdministrationStatus, string> = {
  GIVEN: 'bg-emerald-100 text-emerald-800',
  REFUSED: 'bg-rose-100 text-rose-800',
  HELD: 'bg-amber-100 text-amber-800',
  MISSED: 'bg-red-100 text-red-800',
};

export function MedicationListPage() {
  const { clientId } = useParams<{ clientId: string }>();
  const [filterStatus, setFilterStatus] = useState<'ALL' | MedicationStatus>('ACTIVE');
  const [activeTab, setActiveTab] = useState<'board' | 'prn' | 'audit'>('board');
  const [selectedMedicationId, setSelectedMedicationId] = useState<string | null>(null);
  const [form, setForm] = useState<AdministrationForm>(initialForm);
  const [administrations, setAdministrations] = useState<MedicationAdministration[]>([]);

  const { data: medications = [], isLoading, error } = useQuery({
    queryKey: ['medications', clientId],
    queryFn: () => fetchMedications(clientId!),
    enabled: !!clientId,
    refetchInterval: 60000,
  });

  const selectedMedication = medications.find((medication) => medication.id === selectedMedicationId) ?? null;
  const filteredMedications =
    filterStatus === 'ALL'
      ? medications
      : medications.filter((medication) => medication.status === filterStatus);

  const generatedMarRecords = useMemo<MedicationAdministration[]>(() => {
    return medications.slice(0, 4).map((medication, index) => ({
      id: `local-mar-${medication.id}-${index}`,
      medicationId: medication.id,
      clientId: medication.clientId,
      medicationName: medication.medicationName,
      administeredBy: index === 2 ? 'Amelia Patel' : 'Adrian Popescu',
      administeredAt: `${new Date(Date.now() - (index + 1) * 6 * 60 * 60 * 1000).toISOString()}`,
      dosageGiven: medication.dosage,
      status: index === 2 ? 'MISSED' : index === 3 ? 'REFUSED' : 'GIVEN',
      notes: index === 2 ? 'Dose not available during visit. Coordinator notified.' : 'Recorded in local eMAR.',
      refusalReason: index === 3 ? 'Client declined and requested review later.' : undefined,
      prnReason: medication.prnProtocol ? 'Mild pain reported before lunch.' : undefined,
      painScore: medication.prnProtocol ? '4' : undefined,
      effectivenessReview: medication.prnProtocol ? 'Review due after 60 minutes.' : undefined,
    }));
  }, [medications]);

  const marRecords = [...administrations, ...generatedMarRecords].sort(
    (a, b) => new Date(b.administeredAt).getTime() - new Date(a.administeredAt).getTime()
  );

  const overdueCount = medications.filter((medication) => isOverdue(medication)).length;
  const prnCount = medications.filter((medication) => Boolean(medication.prnProtocol)).length;
  const warningCount = medications.filter((medication) => (medication.warnings?.length ?? 0) > 0).length;
  const missedCount = marRecords.filter((record) => record.status === 'MISSED').length;

  const allSafetyChecked = safetyFields.every((field) => form[field]);
  const prnRequired = selectedMedication?.prnProtocol && form.status === 'GIVEN';
  const prnComplete = !prnRequired || (form.prnReason.trim() && form.painScore.trim());
  const refusalComplete = form.status !== 'REFUSED' || form.refusalReason.trim();
  const canRecord = Boolean(selectedMedication) && allSafetyChecked && prnComplete && refusalComplete;

  const openAdministration = (medicationId: string, status: AdministrationStatus = 'GIVEN') => {
    setSelectedMedicationId(medicationId);
    setForm({ ...initialForm, status });
  };

  const recordAdministration = () => {
    if (!selectedMedication || !clientId || !canRecord) return;

    const now = new Date();
    const followUpDueAt = selectedMedication.prnProtocol && form.status === 'GIVEN'
      ? new Date(now.getTime() + 60 * 60 * 1000).toISOString()
      : undefined;

    setAdministrations((records) => [
      {
        id: `admin-${Date.now()}`,
        medicationId: selectedMedication.id,
        clientId,
        medicationName: selectedMedication.medicationName,
        administeredBy: 'Badan Catalin',
        administeredAt: now.toISOString(),
        dosageGiven: selectedMedication.dosage,
        status: form.status,
        notes: form.notes || undefined,
        refusalReason: form.refusalReason || undefined,
        prnReason: form.prnReason || undefined,
        painScore: form.painScore || undefined,
        bodyTemperature: form.bodyTemperature || undefined,
        effectivenessReview: form.effectivenessReview || undefined,
        followUpDueAt,
      },
      ...records,
    ]);

    setSelectedMedicationId(null);
    setForm(initialForm);
    setActiveTab('audit');
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-[#6c3a80]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <p className="text-red-800">Failed to load medications. Please try again.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6 px-4 py-8 sm:px-6 lg:px-8">
      <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <div className="flex items-center gap-3">
              <div className="rounded-md bg-[#6c3a80]/10 p-3 text-[#6c3a80]">
                <Pill className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm font-semibold uppercase tracking-wide text-[#6c3a80]">
                  Medication Safety
                </p>
                <h1 className="text-3xl font-bold text-slate-950">eMAR</h1>
              </div>
            </div>
            <p className="mt-4 max-w-3xl text-sm leading-6 text-slate-600">
              Record every medication support event, including PRN reason, refusals, held doses,
              missed doses, follow-up checks and MAR audit evidence.
            </p>
          </div>
          <Link
            to={`/clients/${clientId}/medications/add`}
            className="inline-flex h-10 items-center justify-center rounded-md bg-[#6c3a80] px-4 text-sm font-semibold text-white transition hover:bg-[#5e3474]"
          >
            Add Medication
          </Link>
        </div>
      </section>

      <section className="grid gap-4 md:grid-cols-4">
        <MetricCard label="PRN protocols" value={String(prnCount)} icon={<ClipboardCheck className="h-5 w-5" />} />
        <MetricCard label="Overdue doses" value={String(overdueCount)} icon={<Clock className="h-5 w-5" />} tone={overdueCount > 0 ? 'warning' : 'default'} />
        <MetricCard label="Warnings" value={String(warningCount)} icon={<AlertTriangle className="h-5 w-5" />} tone={warningCount > 0 ? 'warning' : 'default'} />
        <MetricCard label="Missed records" value={String(missedCount)} icon={<XCircle className="h-5 w-5" />} tone={missedCount > 0 ? 'danger' : 'default'} />
      </section>

      <section className="rounded-lg border border-blue-200 bg-blue-50 p-4">
        <div className="flex gap-3 text-sm leading-6 text-blue-950">
          <ShieldCheck className="mt-0.5 h-5 w-5 flex-shrink-0" />
          <p>
            This eMAR supports NICE/CQC-style record keeping and safety checks. It must be
            clinically reviewed before production medication administration, especially for
            time-critical medicines, covert administration, PRN protocols or dose changes.
          </p>
        </div>
      </section>

      <div className="border-b border-slate-200">
        <nav className="-mb-px flex gap-6">
          {[
            { id: 'board', label: 'eMAR Board' },
            { id: 'prn', label: 'PRN Protocols' },
            { id: 'audit', label: 'MAR Audit' },
          ].map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id as typeof activeTab)}
              className={`border-b-2 px-1 py-4 text-sm font-semibold transition ${
                activeTab === tab.id
                  ? 'border-[#6c3a80] text-[#6c3a80]'
                  : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'board' && (
        <>
          <div className="border-b border-slate-200">
            <nav className="-mb-px flex gap-6">
              {(['ACTIVE', 'ALL', 'DISCONTINUED', 'ON_HOLD'] as const).map((status) => (
                <button
                  key={status}
                  type="button"
                  onClick={() => setFilterStatus(status)}
                  className={`border-b-2 px-1 py-3 text-sm font-medium transition ${
                    filterStatus === status
                      ? 'border-[#6c3a80] text-[#6c3a80]'
                      : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700'
                  }`}
                >
                  {status.replace('_', ' ')}
                </button>
              ))}
            </nav>
          </div>

          {filteredMedications.length === 0 ? (
            <div className="rounded-lg bg-white p-12 text-center shadow">
              <p className="text-lg text-slate-500">No medications found</p>
              <p className="mt-2 text-sm text-slate-400">Add medications to start tracking</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredMedications.map((medication) => (
                <MedicationCard
                  key={medication.id}
                  medication={medication}
                  onOpenAdministration={openAdministration}
                />
              ))}
            </div>
          )}
        </>
      )}

      {activeTab === 'prn' && (
        <section className="grid gap-4 xl:grid-cols-2">
          {medications.filter((medication) => medication.prnProtocol).map((medication) => (
            <article key={medication.id} className="rounded-lg border border-purple-200 bg-white p-5 shadow-sm">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-lg font-bold text-slate-950">{medication.medicationName}</h2>
                  <p className="mt-1 text-sm text-slate-600">{medication.dosage} | {medication.frequency}</p>
                </div>
                <span className="rounded-full bg-purple-100 px-3 py-1 text-xs font-semibold text-purple-800">
                  PRN
                </span>
              </div>
              <div className="mt-4 rounded-md bg-purple-50 p-4 text-sm leading-6 text-purple-900">
                {medication.prnProtocol}
              </div>
              {medication.maxDailyDose && (
                <p className="mt-3 text-sm font-semibold text-slate-800">
                  Max daily dose: {medication.maxDailyDose}
                </p>
              )}
              <ul className="mt-3 space-y-2 text-sm text-slate-700">
                {(medication.safetyChecks ?? []).map((check) => (
                  <li key={check} className="flex gap-2">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600" />
                    {check}
                  </li>
                ))}
              </ul>
              <button
                type="button"
                onClick={() => openAdministration(medication.id)}
                className="mt-4 rounded-md bg-[#6c3a80] px-4 py-2 text-sm font-semibold text-white hover:bg-[#5e3474]"
              >
                Record PRN Administration
              </button>
            </article>
          ))}
        </section>
      )}

      {activeTab === 'audit' && (
        <section className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 p-5">
            <div className="flex items-center gap-3">
              <FileText className="h-5 w-5 text-[#6c3a80]" />
              <h2 className="text-xl font-bold text-slate-950">MAR Audit History</h2>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50 text-left text-xs font-semibold uppercase text-slate-500">
                <tr>
                  <th className="px-4 py-3">Time</th>
                  <th className="px-4 py-3">Medicine</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Dose</th>
                  <th className="px-4 py-3">Recorded by</th>
                  <th className="px-4 py-3">Evidence</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 bg-white">
                {marRecords.map((record) => (
                  <tr key={record.id}>
                    <td className="px-4 py-3 text-slate-600">{formatDateTime(record.administeredAt)}</td>
                    <td className="px-4 py-3 font-medium text-slate-950">{record.medicationName}</td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full px-2 py-1 text-xs font-semibold ${statusStyles[record.status]}`}>
                        {record.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{record.dosageGiven}</td>
                    <td className="px-4 py-3 text-slate-600">{record.administeredBy}</td>
                    <td className="px-4 py-3 text-slate-600">
                      {record.prnReason && <span>PRN: {record.prnReason}. </span>}
                      {record.painScore && <span>Pain score: {record.painScore}. </span>}
                      {record.refusalReason && <span>Refusal: {record.refusalReason}. </span>}
                      {record.followUpDueAt && <span>Follow-up: {formatDateTime(record.followUpDueAt)}. </span>}
                      {record.notes}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {selectedMedication && (
        <section className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 px-4 py-8">
          <div className="max-h-full w-full max-w-3xl overflow-y-auto rounded-lg bg-white shadow-2xl">
            <div className="border-b border-slate-200 p-5">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-sm font-semibold uppercase tracking-wide text-[#6c3a80]">eMAR Record</p>
                  <h2 className="mt-1 text-2xl font-bold text-slate-950">{selectedMedication.medicationName}</h2>
                  <p className="mt-1 text-sm text-slate-600">{selectedMedication.dosage} | {selectedMedication.route} | {selectedMedication.frequency}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedMedicationId(null)}
                  className="rounded-md border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Close
                </button>
              </div>
            </div>

            <div className="space-y-5 p-5">
              <div className="grid gap-3 sm:grid-cols-4">
                {(['GIVEN', 'REFUSED', 'HELD', 'MISSED'] as AdministrationStatus[]).map((status) => (
                  <button
                    key={status}
                    type="button"
                    onClick={() => setForm((current) => ({ ...current, status }))}
                    className={`rounded-md border px-3 py-2 text-sm font-semibold ${
                      form.status === status
                        ? 'border-[#6c3a80] bg-[#6c3a80] text-white'
                        : 'border-slate-300 text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    {status}
                  </button>
                ))}
              </div>

              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                <h3 className="font-semibold text-slate-950">Safety Checklist</h3>
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  {safetyFields.map((field) => (
                    <label key={field} className="flex items-center gap-3 rounded-md bg-white p-3 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        checked={form[field]}
                        onChange={(event) => setForm((current) => ({ ...current, [field]: event.target.checked }))}
                        className="h-4 w-4 rounded border-slate-300 text-[#6c3a80] focus:ring-[#6c3a80]"
                      />
                      {safetyLabels[field]}
                    </label>
                  ))}
                </div>
              </div>

              {selectedMedication.prnProtocol && form.status === 'GIVEN' && (
                <div className="rounded-lg border border-purple-200 bg-purple-50 p-4">
                  <h3 className="font-semibold text-purple-950">PRN Protocol Evidence</h3>
                  <p className="mt-1 text-sm leading-6 text-purple-900">{selectedMedication.prnProtocol}</p>
                  <div className="mt-4 grid gap-4 sm:grid-cols-3">
                    <Field label="Reason for PRN">
                      <input
                        value={form.prnReason}
                        onChange={(event) => setForm((current) => ({ ...current, prnReason: event.target.value }))}
                        className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm"
                        placeholder="Pain, fever, anxiety..."
                      />
                    </Field>
                    <Field label="Pain score 0-10">
                      <input
                        value={form.painScore}
                        onChange={(event) => setForm((current) => ({ ...current, painScore: event.target.value }))}
                        className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm"
                        placeholder="0-10"
                      />
                    </Field>
                    <Field label="Temperature">
                      <div className="relative">
                        <Thermometer className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                        <input
                          value={form.bodyTemperature}
                          onChange={(event) => setForm((current) => ({ ...current, bodyTemperature: event.target.value }))}
                          className="h-10 w-full rounded-md border border-slate-300 pl-9 pr-3 text-sm"
                          placeholder="Optional"
                        />
                      </div>
                    </Field>
                  </div>
                </div>
              )}

              {form.status === 'REFUSED' && (
                <Field label="Refusal reason">
                  <textarea
                    value={form.refusalReason}
                    onChange={(event) => setForm((current) => ({ ...current, refusalReason: event.target.value }))}
                    rows={3}
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                    placeholder="Record exactly why the client refused and who was informed."
                  />
                </Field>
              )}

              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Effectiveness review">
                  <input
                    value={form.effectivenessReview}
                    onChange={(event) => setForm((current) => ({ ...current, effectivenessReview: event.target.value }))}
                    className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm"
                    placeholder="Review after 60 minutes, no adverse reaction..."
                  />
                </Field>
                <Field label="Notes">
                  <input
                    value={form.notes}
                    onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))}
                    className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm"
                    placeholder="Optional notes"
                  />
                </Field>
              </div>

              {!canRecord && (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                  Complete all safety checks, PRN evidence and refusal reason where required before saving this MAR record.
                </div>
              )}

              <button
                type="button"
                onClick={recordAdministration}
                disabled={!canRecord}
                className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-md bg-[#6c3a80] px-4 text-sm font-semibold text-white transition hover:bg-[#5e3474] disabled:cursor-not-allowed disabled:opacity-50"
              >
                <ClipboardCheck className="h-4 w-4" />
                Save MAR Record
              </button>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

function MedicationCard({
  medication,
  onOpenAdministration,
}: {
  medication: Medication;
  onOpenAdministration: (medicationId: string, status?: AdministrationStatus) => void;
}) {
  const overdue = isOverdue(medication);
  const refillNeeded = needsRefill(medication);

  return (
    <article className={`overflow-hidden rounded-lg bg-white shadow ${overdue ? 'border-l-4 border-red-500' : 'border border-slate-200'}`}>
      <div className="p-6">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex flex-1 items-start gap-4">
            <div className="rounded-md bg-[#6c3a80]/10 p-3 text-[#6c3a80]">
              <Pill className="h-6 w-6" />
            </div>
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="text-lg font-semibold text-slate-950">{medication.medicationName}</h3>
                <span className={`rounded-full px-2 py-1 text-xs font-medium ${getMedicationStatusColor(medication.status)}`}>
                  {medication.status}
                </span>
                {medication.prnProtocol && <span className="rounded-full bg-purple-100 px-2 py-1 text-xs font-medium text-purple-800">PRN</span>}
                {refillNeeded && <span className="rounded-full bg-orange-100 px-2 py-1 text-xs font-medium text-orange-800">Refill needed</span>}
                {overdue && <span className="rounded-full bg-red-100 px-2 py-1 text-xs font-medium text-red-800">Overdue</span>}
              </div>

              {medication.genericName && (
                <p className="mt-1 text-sm text-slate-600">Generic: {medication.genericName}</p>
              )}

              <div className="mt-4 grid grid-cols-2 gap-4 text-sm md:grid-cols-4">
                <Detail label="Dosage" value={medication.dosage} />
                <Detail label="Frequency" value={medication.frequency} />
                <Detail label="Route" value={medication.route} />
                <Detail label="Prescribed by" value={medication.prescribedBy} />
              </div>

              {medication.instructions && (
                <div className="mt-4 rounded-md bg-blue-50 p-3">
                  <p className="text-sm text-slate-700">
                    <span className="font-medium">Instructions:</span> {medication.instructions}
                  </p>
                </div>
              )}

              {medication.warnings && medication.warnings.length > 0 && (
                <div className="mt-3 rounded-md border border-yellow-200 bg-yellow-50 p-3">
                  <p className="text-sm font-medium text-yellow-900">Warnings</p>
                  <ul className="mt-1 list-disc space-y-1 pl-5 text-sm text-yellow-800">
                    {medication.warnings.map((warning) => (
                      <li key={warning}>{warning}</li>
                    ))}
                  </ul>
                </div>
              )}

              {(medication.prnProtocol || medication.safetyChecks?.length) && (
                <div className="mt-3 rounded-md border border-purple-200 bg-purple-50 p-3">
                  <p className="text-sm font-semibold text-purple-950">Medication safety</p>
                  {medication.prnProtocol && (
                    <p className="mt-1 text-sm text-purple-900">
                      <span className="font-medium">PRN protocol:</span> {medication.prnProtocol}
                    </p>
                  )}
                  {medication.maxDailyDose && (
                    <p className="mt-1 text-sm text-purple-900">
                      <span className="font-medium">Max daily dose:</span> {medication.maxDailyDose}
                    </p>
                  )}
                  {medication.safetyChecks && medication.safetyChecks.length > 0 && (
                    <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-purple-900">
                      {medication.safetyChecks.map((check) => (
                        <li key={check}>{check}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              <div className="mt-4 grid gap-2 text-sm text-slate-600 sm:grid-cols-2">
                {medication.lastAdministered && (
                  <p>Last given: {formatDateTime(medication.lastAdministered)}</p>
                )}
                {medication.nextScheduledTime && (
                  <p className={overdue ? 'font-medium text-red-600' : 'text-slate-600'}>
                    Next due: {formatDateTime(medication.nextScheduledTime)}
                    {overdue ? ' (overdue)' : ''}
                  </p>
                )}
              </div>
            </div>
          </div>

          {medication.status === 'ACTIVE' && (
            <div className="grid gap-2 sm:grid-cols-2 lg:w-44 lg:grid-cols-1">
              <button
                type="button"
                onClick={() => onOpenAdministration(medication.id, 'GIVEN')}
                className="rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-700"
              >
                Record Given
              </button>
              <button
                type="button"
                onClick={() => onOpenAdministration(medication.id, 'REFUSED')}
                className="rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                Refused
              </button>
              <button
                type="button"
                onClick={() => onOpenAdministration(medication.id, 'HELD')}
                className="rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                Held
              </button>
              <button
                type="button"
                onClick={() => onOpenAdministration(medication.id, 'MISSED')}
                className="rounded-md border border-red-200 px-4 py-2 text-sm font-semibold text-red-700 transition hover:bg-red-50"
              >
                Missed
              </button>
            </div>
          )}
        </div>
      </div>
    </article>
  );
}

function MetricCard({
  label,
  value,
  icon,
  tone = 'default',
}: {
  label: string;
  value: string;
  icon: React.ReactNode;
  tone?: 'default' | 'warning' | 'danger';
}) {
  const colors = {
    default: 'border-slate-200 bg-white text-slate-950',
    warning: 'border-amber-200 bg-amber-50 text-amber-950',
    danger: 'border-red-200 bg-red-50 text-red-950',
  };

  return (
    <div className={`rounded-lg border p-4 shadow-sm ${colors[tone]}`}>
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs font-semibold uppercase text-slate-500">{label}</p>
        <div className="text-[#6c3a80]">{icon}</div>
      </div>
      <p className="mt-2 text-3xl font-bold">{value}</p>
    </div>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-slate-500">{label}</p>
      <p className="font-medium text-slate-950">{value}</p>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-slate-800">{label}</span>
      {children}
    </label>
  );
}

function getMedicationStatusColor(status: MedicationStatus) {
  switch (status) {
    case 'ACTIVE':
      return 'bg-green-100 text-green-800';
    case 'DISCONTINUED':
      return 'bg-gray-100 text-gray-800';
    case 'ON_HOLD':
      return 'bg-yellow-100 text-yellow-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
}

function isOverdue(medication: Medication) {
  if (!medication.nextScheduledTime || medication.status !== 'ACTIVE') return false;
  return new Date(medication.nextScheduledTime) < new Date();
}

function needsRefill(medication: Medication) {
  return medication.refillsRemaining !== undefined && medication.refillsRemaining <= 1;
}

function formatDateTime(value: string) {
  return new Date(value).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}
